import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ElementRef,
  Input,
  Renderer2,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  effect,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { IMAGE_SIZES } from '@shared/classes/image.class';
import { ProductCategory } from '@shared/classes/product-category.class';
import { ProductItem } from '@shared/classes/product-item.class';
import { Product } from '@shared/classes/product.class';
import { CollectionViewService } from '@core/services/system/collection-view.service';
import { LogService } from '@core/services/system/logger/log.service';
import { Image } from '@shared/classes/image.class';
import { ImageUtils } from '@core/services/utils/image.utils';
import { ImageLoaderService } from '@shared/components/ionic-image-loader-v5';
import { REGEXPS } from '@shared/constants';
import { DomController } from '@ionic/angular';
import { PlatformService } from '@core/services/system/platform/platform.service';

interface ImageLoadTask {
  run: () => void;
  abortController: AbortController;
  started: boolean;
  cancelled: boolean;
  released: boolean;
}

@Component({
  selector: 'pos-virtual-product-item',
  template: '',
  styleUrls: ['./virtual-product-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualProductItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() externalProductItem: ProductItem;
  @Input() cellsQuantity: number;
  @Input() cellWidth: number;
  private product: Product;
  private category: ProductCategory;
  private isCategory: boolean = false;
  private isImageActive: boolean = true;
  private readonly logger = this.logService.createLogger('VirtualProductItemComponent');
  private imageLoadAbortController: AbortController;
  private currentImageElement: HTMLImageElement;
  private currentImageLoadTask: ImageLoadTask;
  private isDestroyed = false;
  private static readonly MAX_PARALLEL_IMAGE_LOADS = 4;
  private static pendingQueue: ImageLoadTask[] = [];
  private static activeLoads = 0;
  private cachedTranslations = {
    new: this.translate.instant('common_new'),
    sale: this.translate.instant('common_sale'),
  };

  // Cache DOM elements for reuse
  private cachedElements: {
    wrapper?: HTMLElement;
    imgHolder?: HTMLElement;
    productImage?: HTMLElement;
    placeholder?: HTMLElement;
    itemName?: HTMLElement;
    badge?: HTMLElement;
    categoryBg?: HTMLElement;
    rippleContainer?: HTMLElement;
  } = {};

  private lastRenderedData: {
    name?: string;
    bgColor?: string;
    isNew?: boolean;
    isSale?: boolean;
    isCategory?: boolean;
  } = {};

  constructor(
    private translate: TranslateService,
    private elementRef: ElementRef,
    private imageLoaderService: ImageLoaderService,
    private imageUtils: ImageUtils,
    private renderer: Renderer2,
    private collectionViewService: CollectionViewService,
    private domController: DomController,
    private logService: LogService,
    private platform: PlatformService
  ) {
    this.initProductItemUpdateEffect();
  }

  private initProductItemUpdateEffect(): void {
    const productItemSignal = toSignal(this.collectionViewService.getNewProductItemEvent(), { initialValue: undefined });
    effect(() => {
      const data = productItemSignal();
      if (data === undefined) return;
      if (data?.uuid !== this.externalProductItem?.data?.uuid) return;
      this.setItemAndRender(data);
    });
  }

  ngOnInit() {
    this.isImageActive = this.collectionViewService.getRenderImagesStatus();
    this.isCategory = this.externalProductItem.isCategory;
    this.setWidthToElement(this.cellWidth);
    this.setItemAndRender(this.externalProductItem.data);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const cellWidthChange = changes.cellWidth;
    if (cellWidthChange && !cellWidthChange.isFirstChange()) {
      this.setWidthToElement(cellWidthChange.currentValue);
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.cancelCurrentImageLoadTask();

    // Clear temporary img element
    if (this.currentImageElement) {
      this.currentImageElement.onload = null;
      this.currentImageElement.onerror = null;
      this.currentImageElement.src = '';
      this.currentImageElement = null;
    }

    // Clear cached elements
    this.cachedElements = {};
    this.lastRenderedData = {};
  }

  private setItemAndRender(data: any) {
    this.setItem(data);
    this.render();
  }

  private setItem(data: any) {
    if (this.isCategory) {
      this.category = new ProductCategory(data);
      return;
    }
    this.product = new Product(data);
  }

  private render() {
    if (this.isCategory) {
      this.renderCategory(this.category);
      return;
    }
    this.renderProduct(this.product);
  }

  private setWidthToElement(width: number) {
    this.renderer.setStyle(this.elementRef.nativeElement, 'width', `${width}%`);
  }

  private renderProduct(item: Product) {
    if (!item.visible) {
      return;
    }

    // Check if full structure rebuild is needed
    const needsFullRebuild = !this.cachedElements.wrapper || this.lastRenderedData.isCategory === true;

    if (needsFullRebuild) {
      this.buildProductStructure();
    }

    // Update badge only if changed
    this.updateBadge(item);

    // Update name only if changed
    if (this.lastRenderedData.name !== item.name) {
      this.updateProductName(item.name);
      this.lastRenderedData.name = item.name;
    }

    // Update background color only if changed
    if (this.lastRenderedData.bgColor !== item.bgColor) {
      this.renderer.setStyle(this.cachedElements.imgHolder, 'background-color', item.bgColor);
      this.lastRenderedData.bgColor = item.bgColor;
    }

    this.lastRenderedData.isCategory = false;
    this.renderProductImage(item);
  }

  private buildProductStructure() {
    // Create structure once
    const wrapper = this.renderer.createElement('div');
    this.renderer.addClass(wrapper, 'product-item-wrapper');

    const imgHolder = this.renderer.createElement('div');
    this.renderer.addClass(imgHolder, 'img-holder');

    const productImage = this.renderer.createElement('div');
    this.renderer.addClass(productImage, 'product-item-image');

    const placeholder = this.renderer.createElement('p');
    this.renderer.addClass(placeholder, 'item-placeholder');

    const itemName = this.renderer.createElement('p');
    this.renderer.addClass(itemName, 'item-name');
    const nameSpan = this.renderer.createElement('span');
    this.renderer.appendChild(itemName, nameSpan);

    const rippleContainer = this.renderer.createElement('div');
    this.renderer.addClass(rippleContainer, 'ripple-container');
    const ripple = this.renderer.createElement('div');
    this.renderer.addClass(ripple, 'ripple');
    this.renderer.appendChild(rippleContainer, ripple);

    // Assemble structure
    this.renderer.appendChild(imgHolder, productImage);
    this.renderer.appendChild(imgHolder, placeholder);
    this.renderer.appendChild(wrapper, imgHolder);
    this.renderer.appendChild(wrapper, itemName);
    this.renderer.appendChild(wrapper, rippleContainer);

    // Clear and add to element
    const nativeEl = this.elementRef.nativeElement;
    while (nativeEl.firstChild) {
      this.renderer.removeChild(nativeEl, nativeEl.firstChild);
    }
    this.renderer.appendChild(nativeEl, wrapper);

    // Cache elements
    this.cachedElements = {
      wrapper,
      imgHolder,
      productImage,
      placeholder,
      itemName,
      rippleContainer,
    };
  }

  private updateBadge(item: Product) {
    const needsBadge = (item.isNew && !this.isCategory) || (!item.isNew && item.isSale);
    const badgeChanged =
      this.lastRenderedData.isNew !== item.isNew ||
      this.lastRenderedData.isSale !== item.isSale;

    if (!needsBadge && this.cachedElements.badge) {
      // Remove badge if no longer needed
      this.renderer.removeChild(this.elementRef.nativeElement, this.cachedElements.badge);
      this.cachedElements.badge = null;
    } else if (needsBadge && (!this.cachedElements.badge || badgeChanged)) {
      // Create or update badge
      if (this.cachedElements.badge) {
        this.renderer.removeChild(this.elementRef.nativeElement, this.cachedElements.badge);
      }

      const badge = this.renderer.createElement('span');
      this.renderer.addClass(badge, 'badge');

      if (item.isNew && !this.isCategory) {
        this.renderer.addClass(badge, 'badge-new');
        const text = this.renderer.createText(this.cachedTranslations.new);
        this.renderer.appendChild(badge, text);
      } else if (!item.isNew && item.isSale) {
        this.renderer.addClass(badge, 'badge-sale');
        const text = this.renderer.createText(this.cachedTranslations.sale);
        this.renderer.appendChild(badge, text);
      }

      this.renderer.insertBefore(this.elementRef.nativeElement, badge, this.cachedElements.wrapper);
      this.cachedElements.badge = badge;
    }

    this.lastRenderedData.isNew = item.isNew;
    this.lastRenderedData.isSale = item.isSale;
  }

  private updateProductName(name: string) {
    if (!this.cachedElements.itemName) return;

    const span = this.cachedElements.itemName.querySelector('span');
    if (span) {
      span.textContent = name;
    }

    if (this.cachedElements.placeholder) {
      this.cachedElements.placeholder.textContent = name;
    }
  }

  private renderProductImage(product: Product) {
    const imgHolder = this.cachedElements.productImage;
    if (!imgHolder) return;

    if (product?.images?.length > 0 && this.isImageActive) {
      this.renderImage(imgHolder, product.images[0].image);
    } else {
      this.renderer.removeClass(imgHolder, 'image-loaded');
      this.renderer.setStyle(imgHolder, 'background-image', 'none');
    }
  }

  private renderCategory(item: ProductCategory) {
    // Check if full structure rebuild is needed
    const needsFullRebuild = !this.cachedElements.wrapper || this.lastRenderedData.isCategory === false;

    if (needsFullRebuild) {
      this.buildCategoryStructure();
    }

    // Update name only if changed
    if (this.lastRenderedData.name !== item.name) {
      this.updateProductName(item.name);
      this.lastRenderedData.name = item.name;
    }

    // Update background color only if changed
    if (this.lastRenderedData.bgColor !== item.bgColor) {
      this.renderer.setStyle(this.cachedElements.imgHolder, 'background-color', item.bgColor);
      this.lastRenderedData.bgColor = item.bgColor;
    }

    this.lastRenderedData.isCategory = true;
    this.renderCategoryImage(item);
  }

  private buildCategoryStructure() {
    // Create category structure once
    const categoryBg = this.renderer.createElement('div');
    this.renderer.addClass(categoryBg, 'category-bg');

    const wrapper = this.renderer.createElement('div');
    this.renderer.addClass(wrapper, 'product-item-wrapper');

    const imgHolder = this.renderer.createElement('div');
    this.renderer.addClass(imgHolder, 'img-holder');

    const productImage = this.renderer.createElement('div');
    this.renderer.addClass(productImage, 'product-item-image');

    const placeholder = this.renderer.createElement('p');
    this.renderer.addClass(placeholder, 'item-placeholder');

    const itemName = this.renderer.createElement('p');
    this.renderer.addClass(itemName, 'item-name');
    const nameSpan = this.renderer.createElement('span');
    this.renderer.appendChild(itemName, nameSpan);

    const rippleContainer = this.renderer.createElement('div');
    this.renderer.addClass(rippleContainer, 'ripple-container');

    // Assemble structure
    this.renderer.appendChild(imgHolder, productImage);
    this.renderer.appendChild(imgHolder, placeholder);
    this.renderer.appendChild(wrapper, imgHolder);
    this.renderer.appendChild(wrapper, itemName);
    this.renderer.appendChild(wrapper, rippleContainer);

    // Clear and add to element
    const nativeEl = this.elementRef.nativeElement;
    while (nativeEl.firstChild) {
      this.renderer.removeChild(nativeEl, nativeEl.firstChild);
    }
    this.renderer.appendChild(nativeEl, categoryBg);
    this.renderer.appendChild(nativeEl, wrapper);

    // Cache elements
    this.cachedElements = {
      categoryBg,
      wrapper,
      imgHolder,
      productImage,
      placeholder,
      itemName,
      rippleContainer,
    };
  }

  private renderCategoryImage(category: ProductCategory) {
    const imgHolder = this.cachedElements.productImage;
    if (!imgHolder) return;

    if (category.image && this.isImageActive) {
      this.renderImage(imgHolder, category.image);
    } else {
      this.renderer.removeClass(imgHolder, 'image-loaded');
      this.renderer.setStyle(imgHolder, 'background-image', 'none');
    }
  }

  private renderImage(imgHolder: HTMLElement, image: Image) {
    this.cancelCurrentImageLoadTask();

    const abortController = new AbortController();
    this.imageLoadAbortController = abortController;
    const signal = abortController.signal;

    const imageSize = this.getImageSize();
    const imageUrl = image.getImageUrlBySize(imageSize);

    const task: ImageLoadTask = {
      abortController,
      started: false,
      cancelled: false,
      released: false,
      run: () => {
        if (this.shouldCancelImageTask(task)) {
          this.releaseImageLoadTask(task);
          return;
        }

        this.imageLoaderService
          .getImagePath(imageUrl)
          .then((path) => {
            if (this.shouldCancelImageTask(task)) {
              return null;
            }
            path = path?.replace(/(\r\n|\n|\r)/gm, '');
            path = this.imageUtils.convertFileSrc(path);
            return this.loadImage(path, signal);
          })
          .then((path) => {
            if (!path || this.shouldCancelImageTask(task)) {
              return;
            }
            this.domController.write(() => {
              // NOTE: do not use shouldCancelImageTask() here — by the time this
              // RAF callback fires, the surrounding promise chain has already
              // resolved and `releaseImageLoadTask` (called in .finally) cleared
              // `currentImageLoadTask`, which would make shouldCancelImageTask()
              // return true and the background-image would never be applied.
              // Check only real cancellation signals.
              if (this.isDestroyed || task.cancelled || signal.aborted) {
                return;
              }
              this.renderer.setStyle(imgHolder, 'background-image', `url("${path}")`);
              this.renderer.addClass(imgHolder, 'item-image');
              this.renderer.addClass(imgHolder, 'image-loaded');
            });
          })
          .catch((err) => {
            if (err?.name !== 'AbortError') {
              this.logger.error(err, 'renderImage:ImageLoader.getImagePath:imageUrl', undefined, { imageUrl });
            }
          })
          .finally(() => {
            this.releaseImageLoadTask(task);
          });
      },
    };

    this.currentImageLoadTask = task;
    this.enqueueImageLoad(task);
  }

  private cancelCurrentImageLoadTask(): void {
    const task = this.currentImageLoadTask;
    if (task) {
      task.cancelled = true;
      task.abortController.abort();
      this.removePendingImageLoadTask(task);
      this.releaseImageLoadTask(task, task.started);
      this.currentImageLoadTask = null;
    }

    if (this.imageLoadAbortController) {
      this.imageLoadAbortController.abort();
      this.imageLoadAbortController = null;
    }
  }

  private shouldCancelImageTask(task: ImageLoadTask): boolean {
    return this.isDestroyed || task.cancelled || task.abortController.signal.aborted || this.currentImageLoadTask !== task;
  }

  private releaseImageLoadTask(task: ImageLoadTask, releaseActiveSlot: boolean = true): void {
    if (task.released) {
      return;
    }
    task.released = true;

    if (this.currentImageLoadTask === task) {
      this.currentImageLoadTask = null;
    }
    if (this.imageLoadAbortController === task.abortController) {
      this.imageLoadAbortController = null;
    }

    if (releaseActiveSlot && task.started) {
      VirtualProductItemComponent.activeLoads = Math.max(0, VirtualProductItemComponent.activeLoads - 1);
      this.runNextImageLoadTask();
    }
  }

  private removePendingImageLoadTask(task: ImageLoadTask): void {
    const index = VirtualProductItemComponent.pendingQueue.indexOf(task);
    if (index >= 0) {
      VirtualProductItemComponent.pendingQueue.splice(index, 1);
    }
  }

  private runNextImageLoadTask(): void {
    let next = VirtualProductItemComponent.pendingQueue.shift();
    while (next?.cancelled || next?.abortController.signal.aborted) {
      this.releaseImageLoadTask(next, false);
      next = VirtualProductItemComponent.pendingQueue.shift();
    }
    if (next) {
      VirtualProductItemComponent.activeLoads++;
      next.started = true;
      next.run();
    }
  }

  private enqueueImageLoad(task: ImageLoadTask) {
    if (VirtualProductItemComponent.activeLoads < VirtualProductItemComponent.MAX_PARALLEL_IMAGE_LOADS) {
      VirtualProductItemComponent.activeLoads++;
      task.started = true;
      task.run();
    } else {
      VirtualProductItemComponent.pendingQueue.push(task);
    }
  }

  private getImageSize() {
    const isMobile = this.platform.isMobile;
    return isMobile ? IMAGE_SIZES.MEDIUM : IMAGE_SIZES.NORMAL;
  }

  private loadImage(path: string, signal: AbortSignal) {
    return new Promise<string>((resolve, reject) => {
      if (!REGEXPS.REMOTE_IMAGE_SERVER.test(path)) {
        return resolve(path);
      }

      // Check abort before creating element
      if (signal.aborted) {
        return reject(new DOMException('Aborted', 'AbortError'));
      }

      // Clear previous element if exists
      if (this.currentImageElement) {
        this.currentImageElement.onload = null;
        this.currentImageElement.onerror = null;
        this.currentImageElement.src = '';
        this.currentImageElement = null;
      }

      const imgEl = document.createElement('img');
      this.currentImageElement = imgEl;

      // Save reference to handler for later removal
      const onAbort = () => {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };

      const cleanup = () => {
        // Remove abort listener to prevent memory leak
        signal.removeEventListener('abort', onAbort);
        if (imgEl) {
          imgEl.onload = null;
          imgEl.onerror = null;
          imgEl.src = '';
        }
        if (this.currentImageElement === imgEl) {
          this.currentImageElement = null;
        }
      };

      imgEl.onload = () => {
        cleanup();
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
        } else {
          resolve(path);
        }
      };

      imgEl.onerror = () => {
        cleanup();
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
        } else {
          resolve('');
        }
      };

      // Listen for abort event
      signal.addEventListener('abort', onAbort, { once: true });

      // Start loading
      imgEl.src = path;
    });
  }
}
